import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Ban } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function CancelTicketModal({ open, onOpenChange, ticket, onCancelled }) {
  const [reasons, setReasons] = useState([]);
  const [reasonId, setReasonId] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/cancel-reasons").then((r) => setReasons(r.data.filter((x) => x.active)));
  }, [open]);

  const submit = async () => {
    if (!reasonId) return toast.error("Pick a cancel reason");
    setSubmitting(true);
    try {
      const reason = reasons.find((r) => r.id === reasonId);
      const { data } = await api.post(`/tickets/${ticket.id}/cancel`, {
        cancel_reason_id: reasonId,
        cancel_reason_name: reason?.name || "",
        message,
      });
      toast.success(`${ticket.code} cancelled · POC notified`);
      onCancelled?.(data);
      onOpenChange(false);
      setReasonId(""); setMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cancel-ticket-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-rose-700">
            <Ban className="w-4 h-4" /> Cancel ticket
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            This will move <span className="font-mono">{ticket?.code}</span> to <em>cancelled</em> and
            notify the owner. Cancellation is logged in the activity timeline.
          </p>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Cancel reason *</Label>
            <Select value={reasonId} onValueChange={setReasonId}>
              <SelectTrigger className="mt-1.5" data-testid="cancel-reason-select">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Note (optional)</Label>
            <Textarea
              data-testid="cancel-message-input"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Any context the owner/POC should see..."
              className="mt-1.5 resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-modal-cancel">
            Keep open
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !reasonId}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="cancel-modal-submit"
          >
            <Ban className="w-3.5 h-3.5 mr-1.5" />
            {submitting ? "Cancelling..." : "Cancel ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
